import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, IconButton, Chip, TextField, InputAdornment, Menu, MenuItem } from '@mui/material';
import { Add as AddIcon, MoreVert as MoreVertIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, ToggleOn as ToggleOnIcon, ToggleOff as ToggleOffIcon } from '@mui/icons-material';
import PatientImportDialog from '../components/PatientImportDialog';
import { Patient, PatientFilterParams } from '../types/patient.types';
import { PatientService } from '../services/patient.service';

const PatientListPage: React.FC = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  // Fetch patients with filters and pagination
  const fetchPatients = async () => {
    try {
      setLoading(true);
      const params: PatientFilterParams = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
      };
      
      const response = await PatientService.getPatients(params);
      setPatients(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('Error fetching patients:', error);
      // Handle error (e.g., show toast)
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, [page, rowsPerPage, searchTerm]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, patient: Patient) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPatient(null);
  };

  const handleViewPatient = (id: string) => {
    navigate(`/patient-management/patients/${id}`);
    handleMenuClose();
  };

  const handleEditPatient = (id: string) => {
    navigate(`/patient-management/patients/${id}/edit`);
    handleMenuClose();
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await PatientService.togglePatientStatus(id, !currentStatus);
      fetchPatients(); // Refresh the list
    } catch (error) {
      console.error('Error toggling patient status:', error);
      // Handle error
    }
    handleMenuClose();
  };

  const handleDeletePatient = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this patient?')) {
      try {
        await PatientService.deletePatient(id);
        fetchPatients(); // Refresh the list
      } catch (error) {
        console.error('Error deleting patient:', error);
        // Handle error
      }
    }
    handleMenuClose();
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Patients
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/patient-management/patients/new')}
          >
            Add Patient
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setImportOpen(true)}
          >
            Import Patients
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>


          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>MRN</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : patients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No patients found
                    </TableCell>
                  </TableRow>
                ) : (
                  patients.map((patient) => (
                    <TableRow key={patient.id} hover>
                      <TableCell>{patient.mrn}</TableCell>
                      <TableCell>{patient.fullName}</TableCell>
                      <TableCell>{patient.age}</TableCell>
                      <TableCell>{patient.gender}</TableCell>
                      <TableCell>
                        {patient.phoneNumber && <div>{patient.phoneNumber}</div>}
                        {patient.email && <div>{patient.email}</div>}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={patient.isActive ? 'Active' : 'Inactive'}
                          color={patient.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, patient)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>


          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
      >
        <MenuItem onClick={() => selectedPatient && handleViewPatient(selectedPatient.id)}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => selectedPatient && handleEditPatient(selectedPatient.id)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        {selectedPatient && (
          <MenuItem onClick={() => handleToggleStatus(selectedPatient.id, selectedPatient.isActive)}>
            {selectedPatient.isActive ? (
              <>
                <ToggleOffIcon fontSize="small" sx={{ mr: 1 }} />
                Deactivate
              </>
            ) : (
              <>
                <ToggleOnIcon fontSize="small" sx={{ mr: 1 }} />
                Activate
              </>
            )}
          </MenuItem>
        )}
        <MenuItem 
          onClick={() => selectedPatient && handleDeletePatient(selectedPatient.id)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
      <PatientImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportSuccess={fetchPatients}
      />
    </Container>
  );
};

export default PatientListPage;
